import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { EyeIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, formatCurrency, formatDate, getStatusColor, getStatusLabel, getOfferTypeLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Subscriptions = ({ admin = false }) => {
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Récupérer les souscriptions
  const { data: subscriptionsData, isLoading: dataLoading } = useQuery(
    admin ? 'admin-subscriptions' : 'user-subscriptions',
    () => admin ? subscriptionAPI.getAllSubscriptions() : subscriptionAPI.getSubscriptions()
  );

  const subscriptions = subscriptionsData?.data?.subscriptions || [];

  const handleCancelSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    if (!selectedSubscription) return;

    setIsLoading(true);
    try {
      await subscriptionAPI.cancelSubscription(selectedSubscription.id, {
        cancellationReason: cancellationReason || 'Demande d\'annulation utilisateur'
      });
      
      toast.success('Souscription annulée avec succès');
      queryClient.invalidateQueries(admin ? 'admin-subscriptions' : 'user-subscriptions');
      setShowCancelModal(false);
      setSelectedSubscription(null);
      setCancellationReason('');
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'annulation');
    } finally {
      setIsLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          {admin ? 'Gestion des souscriptions' : 'Mes souscriptions'}
        </h1>
        <p className="text-gray-600">
          {admin 
            ? 'Gérez toutes les souscriptions des mutualistes'
            : 'Consultez et gérez vos souscriptions actives'
          }
        </p>
      </div>

      {/* Filtres */}
      {admin && (
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Recherche</label>
              <input
                type="text"
                placeholder="Nom, email..."
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Statut</label>
              <select className="form-input">
                <option value="">Tous les statuts</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">En attente</option>
                <option value="SUSPENDED">Suspendu</option>
                <option value="CANCELLED">Annulé</option>
              </select>
            </div>
            <div>
              <label className="form-label">Type de produit</label>
              <select className="form-input">
                <option value="">Tous les types</option>
                <option value="HEALTH_COVERAGE">Couverture santé</option>
                <option value="SAVINGS">Épargne</option>
                <option value="LOAN">Prêt</option>
              </select>
            </div>
            <div>
              <label className="form-label">Période</label>
              <input
                type="date"
                className="form-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Liste des souscriptions */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {subscriptions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {admin ? 'Aucune souscription trouvée' : 'Vous n\'avez aucune souscription active'}
            </p>
            {!admin && (
              <Link to="/offers" className="btn-primary">
                Découvrir nos produits
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {admin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mutualiste
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de souscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    {admin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {subscription.user?.firstName?.[0]}{subscription.user?.lastName?.[0]}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {subscription.user?.firstName} {subscription.user?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {subscription.user?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.offer?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {subscription.offer?.description?.substring(0, 50)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">
                          {subscription.offer?.type === 'HEALTH_COVERAGE' && '🏥'}
                          {subscription.offer?.type === 'SAVINGS' && '💰'}
                          {subscription.offer?.type === 'LOAN' && '💳'}
                        </span>
                        <span className="text-sm text-gray-900">
                          {getOfferTypeLabel(subscription.offer?.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(subscription.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(subscription.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                        {getStatusLabel(subscription.status?.libelle || subscription.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/subscriptions/${subscription.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                        {(subscription.status?.libelle || subscription.status) === 'En attente' && (
                          <button
                            onClick={() => handleCancelSubscription(subscription)}
                            className="text-red-600 hover:text-red-900"
                            title="Annuler la souscription"
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {subscriptions.length > 0 && (
        <div className="bg-white rounded-xl shadow-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de 1 à {subscriptions.length} sur {subscriptions.length} résultats
            </div>
            <div className="flex space-x-2">
              <button className="btn-secondary btn-sm" disabled>
                Précédent
              </button>
              <button className="btn-secondary btn-sm" disabled>
                Suivant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'annulation */}
      {showCancelModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCancelModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Annuler la souscription
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Vous êtes sur le point d'annuler la souscription pour{' '}
                        <strong>{selectedSubscription.offer?.name}</strong>.
                        Cette action est irréversible.
                      </p>
                      <div className="mt-4">
                        <label className="form-label">Raison de l'annulation (optionnel)</label>
                        <textarea
                          value={cancellationReason}
                          onChange={(e) => setCancellationReason(e.target.value)}
                          className="form-input"
                          rows="3"
                          placeholder="Expliquez la raison de l'annulation..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmCancellation}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Annuler la souscription
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationReason('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
